import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InsightsIcon from '@mui/icons-material/Insights';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentsIcon from '@mui/icons-material/Payments';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type {
    CampaignReportContentSort,
    CampaignReportPlatform,
    CampaignReportWindow,
    ICampaignReportContentItem,
    ICampaignReportCreatorItem,
    ICampaignReportSummary,
    CampaignReportCreatorSort,
} from '../../../types/global/campaignReport.types';
import { getCampaignReportBoostDisabledReason, isCampaignReportBoostable } from '../../../utils/campaignReportBoost';
import {
    formatReportCompactNumber,
    formatReportPercent,
    formatReportUsd,
    formatWatchHours,
    platformLabel,
    platformShortLabel,
    syncStatusLabel,
} from '../../../utils/campaignReportFormat';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_OUTLINE_VARIANT,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_LOWEST,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';
import { CampaignReportPagination } from './CampaignReportPagination';

function CampaignReportBoostButton({
    onClick,
    variant = 'gold',
    disabled = false,
    disabledReason,
}: {
    onClick: () => void;
    variant?: 'gold' | 'muted';
    disabled?: boolean;
    disabledReason?: string | null;
}) {
    const isGold = variant === 'gold' && !disabled;
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            title={disabledReason || 'Boost as Meta Reels draft'}
            sx={{
                flexShrink: 0,
                minWidth: 92,
                px: 2.5,
                py: 1.15,
                borderRadius: 999,
                textTransform: 'uppercase',
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: 800,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: isGold ? BRAND_ON_PRIMARY_FIXED : '#fff',
                background: isGold
                    ? `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`
                    : BRAND_SURFACE_CONTAINER_HIGHEST,
                boxShadow: isGold ? '0 4px 16px rgba(255, 201, 101, 0.28)' : 'none',
                border: isGold ? 'none' : `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.35)',
                    background: BRAND_SURFACE_CONTAINER_HIGHEST,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                    opacity: 0.7,
                },
                '&:hover': {
                    background: isGold
                        ? `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`
                        : BRAND_SURFACE_CONTAINER_HIGHEST,
                    filter: disabled ? undefined : 'brightness(1.06)',
                },
            }}>
            Boost
        </Button>
    );
}

const WINDOW_OPTIONS: { value: CampaignReportWindow; label: string }[] = [
    { value: '7d', label: '7d' },
    { value: '14d', label: '14d' },
    { value: '30d', label: '30d' },
    { value: 'all_time', label: 'All' },
];

const PLATFORM_OPTIONS: CampaignReportPlatform[] = ['instagram', 'tiktok', 'youtube'];

function glassCardSx(highlight = false) {
    return {
        p: 3,
        borderRadius: 3,
        bgcolor: 'rgba(26, 25, 25, 0.6)',
        border: highlight ? `1px solid ${BRAND_LUXE_PRIMARY}33` : `1px solid ${BRAND_OUTLINE_VARIANT}33`,
        position: 'relative' as const,
        overflow: 'hidden',
    };
}

export function CampaignReportWindowChips({
    value,
    onChange,
}: {
    value: CampaignReportWindow;
    onChange: (w: CampaignReportWindow) => void;
}) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {WINDOW_OPTIONS.map(opt => (
                <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        height: 36,
                        px: 1,
                        bgcolor: value === opt.value ? BRAND_LUXE_PRIMARY : '#201f1f',
                        color: value === opt.value ? '#392700' : 'rgba(255,255,255,0.55)',
                        '&:hover': { bgcolor: value === opt.value ? BRAND_LUXE_PRIMARY : '#262626' },
                    }}
                />
            ))}
        </Box>
    );
}

export function CampaignReportPlatformChips({
    active,
    onToggle,
}: {
    active: CampaignReportPlatform | null;
    onToggle: (p: CampaignReportPlatform) => void;
}) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PLATFORM_OPTIONS.map(p => (
                <Chip
                    key={p}
                    label={platformShortLabel(p)}
                    onClick={() => onToggle(p)}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        bgcolor: active === p ? `${BRAND_LUXE_PRIMARY}22` : BRAND_SURFACE_CONTAINER,
                        color: active === p ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.65)',
                        border: `1px solid ${active === p ? `${BRAND_LUXE_PRIMARY}55` : `${BRAND_OUTLINE_VARIANT}22`}`,
                    }}
                />
            ))}
        </Box>
    );
}

export function CampaignReportExecutiveSummary({ summary }: { summary: ICampaignReportSummary }) {
    const { health, performance, roi } = summary;
    const cards = [
        {
            title: 'Campaign health',
            icon: <FavoriteIcon sx={{ fontSize: 100, opacity: 0.06 }} />,
            stats: [
                { label: 'Creators', value: formatReportCompactNumber(health.approved_participants) },
                { label: 'Published', value: formatReportCompactNumber(health.content_published) },
                { label: 'Posts', value: formatReportCompactNumber(performance.posts_tracked) },
            ],
        },
        {
            title: 'Performance',
            icon: <InsightsIcon sx={{ fontSize: 100, opacity: 0.06 }} />,
            stats: [
                { label: 'Views', value: formatReportCompactNumber(performance.views) },
                { label: 'ER', value: formatReportPercent(performance.engagement_rate), accent: true },
                { label: 'Watch hrs', value: formatWatchHours(performance.watch_time_seconds) },
            ],
        },
        {
            title: 'ROI',
            icon: <PaymentsIcon sx={{ fontSize: 100, opacity: 0.06 }} />,
            stats: [
                { label: 'Spend', value: formatReportUsd(roi.campaign_spend_usd) },
                { label: 'CPV', value: roi.cpv != null ? formatReportUsd(roi.cpv) : '—' },
                { label: 'CPE', value: roi.cpe != null ? formatReportUsd(roi.cpe) : '—' },
            ],
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: BRAND_LUXE_PRIMARY }}>
                Executive summary
            </Typography>
            {cards.map(card => (
                <Box key={card.title} sx={glassCardSx(true)}>
                    <Box sx={{ position: 'absolute', right: -8, top: -8 }}>{card.icon}</Box>
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.5)',
                            mb: 2,
                        }}>
                        {card.title}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                        {card.stats.map(stat => (
                            <Box key={stat.label}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 24,
                                        color: stat.accent ? BRAND_LUXE_SECONDARY : '#fff',
                                    }}>
                                    {stat.value}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                    {stat.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export function CampaignReportPlatformBreakdown({ summary }: { summary: ICampaignReportSummary }) {
    const entries = Object.entries(summary.platform_breakdown ?? {}) as [
        CampaignReportPlatform,
        { posts: number; views: number; reach?: number; engagements: number },
    ][];
    if (!entries.length) return null;
    const maxViews = Math.max(...entries.map(([, s]) => s.views ?? s.reach ?? 0), 1);

    const borderColors: Record<string, string> = {
        instagram: BRAND_LUXE_PRIMARY,
        tiktok: BRAND_LUXE_SECONDARY,
        youtube: '#ff7351',
    };

    const platformOrder: CampaignReportPlatform[] = ['instagram', 'tiktok', 'youtube'];
    const sortedEntries = [...entries].sort((a, b) => platformOrder.indexOf(a[0]) - platformOrder.indexOf(b[0]));
    const columnCount = Math.max(sortedEntries.length, 1);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                Platform breakdown
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                    gap: 2,
                    width: '100%',
                }}>
                {sortedEntries.map(([platform, stats]) => {
                    const metric = stats.views ?? stats.reach ?? 0;
                    return (
                        <Box
                            key={platform}
                            sx={{
                                width: '100%',
                                minWidth: 0,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                borderLeft: `4px solid ${borderColors[platform] ?? BRAND_OUTLINE_VARIANT}`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    color: 'rgba(255,255,255,0.5)',
                                    mb: 1,
                                }}>
                                {platformShortLabel(platform)}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: '#fff' }}>
                                {formatReportCompactNumber(metric)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                {stats.reach != null ? 'Reach' : 'Views'}
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1.5,
                                    height: 4,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    borderRadius: 999,
                                    overflow: 'hidden',
                                }}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        width: `${(metric / maxViews) * 100}%`,
                                        bgcolor: borderColors[platform] ?? BRAND_LUXE_PRIMARY,
                                        borderRadius: 999,
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

const CONTENT_SORTS: { value: CampaignReportContentSort; label: string }[] = [
    { value: 'impact_score', label: 'Impact' },
    { value: 'views', label: 'Views' },
    { value: 'engagements', label: 'Engagements' },
    { value: 'engagement_rate', label: 'ER' },
];

export function CampaignReportContentSortChips({
    value,
    onChange,
}: {
    value: CampaignReportContentSort;
    onChange: (s: CampaignReportContentSort) => void;
}) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {CONTENT_SORTS.map(opt => (
                <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        bgcolor: value === opt.value ? BRAND_LUXE_PRIMARY : '#201f1f',
                        color: value === opt.value ? '#392700' : 'rgba(255,255,255,0.55)',
                    }}
                />
            ))}
        </Box>
    );
}

export function CampaignReportContentList({
    items,
    onBoost,
    page,
    totalPages,
    total,
    onPageChange,
    contentSort,
    onContentSortChange,
}: {
    items: ICampaignReportContentItem[];
    onBoost: (item: ICampaignReportContentItem) => void;
    page: number;
    totalPages: number;
    total: number;
    onPageChange: (page: number) => void;
    contentSort: CampaignReportContentSort;
    onContentSortChange: (s: CampaignReportContentSort) => void;
}) {
    if (!items.length && total === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 1 }}>
                    Top performance
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', mb: 1.5 }}>
                    One row per live URL — boost video submissions as Meta Reels drafts (photo posts are not eligible).
                </Typography>
                <CampaignReportContentSortChips value={contentSort} onChange={onContentSortChange} />
            </Box>
            {items.map((item, index) => {
                const highlighted = index < 2 && page === 1;
                const canBoost = isCampaignReportBoostable(item);
                const boostDisabledReason = getCampaignReportBoostDisabledReason(item);
                const syncNote =
                    (item.sync_status === 'failed' || item.sync_status === 'unsupported') && item.last_sync_error
                        ? item.last_sync_error
                        : item.sync_status === 'unsupported'
                        ? 'Creator needs connected OAuth for this platform'
                        : null;
                return (
                    <Box
                        key={item.id}
                        sx={{
                            ...glassCardSx(highlighted),
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            border: highlighted
                                ? `1px solid ${BRAND_LUXE_PRIMARY}55`
                                : `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2,
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        border: highlighted
                                            ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                            : `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                                    }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff' }}>
                                        {item.creator_name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                                        {platformLabel(String(item.platform))} · {syncStatusLabel(item.sync_status)}
                                    </Typography>
                                    {item.live_url ? (
                                        <Typography
                                            component="a"
                                            href={item.live_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mt: 0.5,
                                                fontSize: 11,
                                                color: BRAND_LUXE_PRIMARY,
                                                textDecoration: 'none',
                                                maxWidth: '100%',
                                            }}>
                                            <OpenInNewIcon sx={{ fontSize: 12 }} />
                                            View post
                                        </Typography>
                                    ) : null}
                                    {syncNote ? (
                                        <Typography sx={{ fontSize: 11, color: '#ff8a65', mt: 0.5 }}>
                                            {syncNote}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                            <Chip
                                size="small"
                                icon={
                                    <BoltIcon
                                        sx={{ fontSize: 14, color: highlighted ? BRAND_LUXE_SECONDARY : 'inherit' }}
                                    />
                                }
                                label={`${Math.round(item.impact_score)} IMPACT`}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    flexShrink: 0,
                                    bgcolor: highlighted
                                        ? `${BRAND_LUXE_SECONDARY}22`
                                        : BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: highlighted ? BRAND_LUXE_SECONDARY : 'rgba(255,255,255,0.65)',
                                    border: highlighted ? `1px solid ${BRAND_LUXE_SECONDARY}44` : 'none',
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: { xs: 'stretch', sm: 'flex-end' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                                        {formatReportCompactNumber(item.metrics?.views ?? 0)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                        Views
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 18,
                                            color: BRAND_LUXE_SECONDARY,
                                        }}>
                                        {formatReportPercent(item.metrics?.engagement_rate ?? 0)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                        Eng. rate
                                    </Typography>
                                </Box>
                            </Box>
                            <CampaignReportBoostButton
                                onClick={() => {
                                    if (canBoost) onBoost(item);
                                }}
                                variant={highlighted ? 'gold' : 'muted'}
                                disabled={!canBoost}
                                disabledReason={boostDisabledReason}
                            />
                        </Box>
                    </Box>
                );
            })}
            <CampaignReportPagination
                page={page}
                totalPages={totalPages}
                total={total}
                onPageChange={onPageChange}
                label="posts"
            />
        </Box>
    );
}

const CREATOR_SORTS: { value: CampaignReportCreatorSort; label: string }[] = [
    { value: 'impact_score', label: 'Top impact' },
    { value: 'views', label: 'Top views' },
    { value: 'engagements', label: 'Top engagement' },
];

export function CampaignReportCreatorsLeaderboard({
    creators,
    sort,
    onSortChange,
    page,
    totalPages,
    total,
    onPageChange,
}: {
    creators: ICampaignReportCreatorItem[];
    sort: CampaignReportCreatorSort;
    onSortChange: (s: CampaignReportCreatorSort) => void;
    page: number;
    totalPages: number;
    total: number;
    onPageChange: (page: number) => void;
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 28, color: '#fff', mb: 0.5 }}>
                    Top performing creators
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
                    Impact scores combine reach, engagement rate, watch time, and raw engagements.
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {CREATOR_SORTS.map(opt => (
                    <Chip
                        key={opt.value}
                        label={opt.label}
                        onClick={() => onSortChange(opt.value)}
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            bgcolor: sort === opt.value ? BRAND_LUXE_PRIMARY : '#201f1f',
                            color: sort === opt.value ? '#392700' : 'rgba(255,255,255,0.55)',
                        }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {creators.map(creator => {
                    const medal = creator.rank === 1 ? BRAND_LUXE_PRIMARY : creator.rank === 2 ? '#94a3b8' : '#c2410c';
                    return (
                        <Box
                            key={creator.influencer_id}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                border:
                                    creator.rank === 1
                                        ? `1px solid ${BRAND_LUXE_PRIMARY}33`
                                        : `1px solid ${BRAND_OUTLINE_VARIANT}22`,
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 2,
                                }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '50%',
                                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                                border: `2px solid ${
                                                    creator.rank === 1
                                                        ? `${BRAND_LUXE_PRIMARY}88`
                                                        : `${BRAND_OUTLINE_VARIANT}44`
                                                }`,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -4,
                                                right: -4,
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                bgcolor: medal,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <WorkspacePremiumIcon
                                                sx={{ fontSize: 14, color: creator.rank === 1 ? '#392700' : '#fff' }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: '#fff' }}>
                                            {creator.creator_name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                                            {creator.platforms.join(' · ')}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 26,
                                            color: creator.rank === 1 ? BRAND_LUXE_PRIMARY : '#fff',
                                        }}>
                                        {Math.round(creator.impact_score)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: 'rgba(255,255,255,0.45)',
                                        }}>
                                        Impact score
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 1,
                                    py: 1.5,
                                    px: 2,
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOWEST,
                                    borderRadius: 2,
                                    mb: 1.5,
                                }}>
                                {[
                                    { label: 'Posts', value: creator.posts },
                                    { label: 'Views', value: formatReportCompactNumber(creator.views) },
                                    { label: 'ER', value: formatReportPercent(creator.engagement_rate), accent: true },
                                ].map((cell, i) => (
                                    <Box
                                        key={cell.label}
                                        sx={{
                                            textAlign: 'center',
                                            borderRight: i < 2 ? `1px solid ${BRAND_OUTLINE_VARIANT}22` : undefined,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                color: cell.accent ? BRAND_LUXE_SECONDARY : '#fff',
                                            }}>
                                            {cell.value}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 9,
                                                textTransform: 'uppercase',
                                                color: 'rgba(255,255,255,0.45)',
                                            }}>
                                            {cell.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                                Payout {formatReportUsd(creator.payout_amount_cents, true)} · Boost from Dashboard posts
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <CampaignReportPagination
                page={page}
                totalPages={totalPages}
                total={total}
                onPageChange={onPageChange}
                label="creators"
            />
        </Box>
    );
}
